import { filter, get, map } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CropStateType, elementBeanListType } from '../../Pages/Flo/Flo.types';
import { calculateAspectRatioFit, calculateScaleValue } from '../../utils/video.utils';
import { scaleLinear } from 'd3-scale';
import styles from './TextElementShowUntil.module.css';
import DynamicFontLoader from './TextFontLoader';

const defaultTexts: elementBeanListType[] = [];

const TextElementShowUntil = ({
	currentTimeSeconds,
	videoSize = {
		width: 0,
		height: 0,
	},
	width: vWidth,
	height: vHeight,
	isCropped,
	appliedCrop,
	elementType,
	floIndex,
}: {
	currentTimeSeconds: number;
	videoSize?:
		| {
				width: number;
				height: number;
		  }
		| undefined
		| null;
	width: number;
	height: number;
	isCropped?: boolean;
	appliedCrop?: CropStateType;
	elementType?: string;
	floIndex: number;
}) => {
	const frameDims = calculateAspectRatioFit(
		get(videoSize, 'width', 0),
		get(videoSize, 'height', 0),
		vWidth,
		vHeight
	);
	const [texts, setTexts] = useState<elementBeanListType[]>([]);
	const offsetLeft = Math.abs(frameDims.width - vWidth) / 2;
	const offsetTop = (vHeight - frameDims.height) / 2;
	const elementsConfig: elementBeanListType[] = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.editorsElements.${elementType}`, defaultTexts)
	);
	const { addFont } = DynamicFontLoader();

	const calculateNewFontSize = useCallback(
		(originalFontSize: number, width: number, height: number): number => {
			// Calculate scaling factors for width and height
			const widthScale = vWidth / width;
			const heightScale = vHeight / height;

			// Use the average of the scaling factors to maintain proportionality
			const averageScale = (widthScale + heightScale) / 2;

			// Calculate the new font size
			const newFontSize = originalFontSize * averageScale;

			return newFontSize;
		},
		[vWidth, vHeight]
	);

	const calCropFontSize = useCallback(
		(
			originalFontSize: number,
			fontCropWidth: number,
			fontCropHeight: number,
			fontEleWidth: number,
			fontEleHeight: number
		): number => {
			const scaleX = vWidth / fontCropWidth;
			const scaleY = vHeight / fontCropHeight;

			// 2. Calculate new parent dimensions
			const newWidth = fontEleWidth * scaleX;
			const newHeight = fontEleHeight * scaleY;

			// Calculate scaling factors for width and height
			const widthScale = newWidth / fontEleWidth;
			const heightScale = newHeight / fontEleHeight;

			// Use the average of the scaling factors to maintain proportionality
			const averageScale = (widthScale + heightScale) / 2;

			// Calculate the new font size
			const newFontSize = originalFontSize * averageScale;

			return newFontSize;
		},
		[vWidth, vHeight]
	);

	// const appliedCrop = useSelector((state) => {
	// 	return find(get(state, 'floPage.editorsElementsConfig'), [
	// 		'editorElementName',
	// 		'crop'
	// 	]);
	// })
	useEffect(() => {
		let textElements = filter(
			elementsConfig,
			(element) =>
				// get(element, 'enabled') &&
				element.editorElementName === elementType &&
				get(element, 'behaviourType') === 'showUntil' &&
				currentTimeSeconds >= (get(element, 'time') ?? -1) &&
				currentTimeSeconds <= (get(element, 'endTime') ?? -1)
		);
		textElements.forEach((element) => {
			addFont(get(element, 'fontName', 'Inter'));
		});
		setTexts(
			map(textElements, (i) => ({
				id: get(i, 'id'),
				text: get(i, 'text'),
				enabled: get(i, 'enabled', true),
				startTime: get(i, 'time'),
				endTime: get(i, 'endTime'),
				duration: get(i, 'duration'),
				fontName: get(i, 'fontName'),
				fontSize: get(i, 'fontSize'),
				cropped: get(i, 'cropped'),
				fontCropData: get(i, 'fontCropData'),
				animation: get(i, 'animation'),
				angle: get(i, 'canvasRenderConfig.canvasCoordinates.angle'),
				fontColor: get(i, 'fontColor'),
				canvasRenderConfig: get(i, 'canvasRenderConfig'),
				aspectRatio: get(i, 'aspectRatio') || get(i, 'canvasRenderConfig.aspectRatio'),
			}))
		);
	}, [setTexts, elementsConfig, currentTimeSeconds, appliedCrop]);

	return map(texts, (element) => (
		<div
			className={styles.textElementContainer}
			style={{
				width: vWidth,
				height: vHeight,
			}}
		>
			<div
				key={get(element, 'id')} // Added key for each element
				className={styles.sizeCalculator}
				style={{
					fontSize: `${calculateNewFontSize(
						element.fontSize,
						get(element, 'canvasRenderConfig.windowCoordinates.width', 0),
						get(element, 'canvasRenderConfig.windowCoordinates.height', 0)
					)}px`,
					color: element.fontColor,
					transform: `translate(${calculateScaleValue(
						get(element, 'canvasRenderConfig.windowCoordinates.width'),
						vWidth,
						get(element, 'canvasRenderConfig.canvasCoordinates.x', 0)
					)}px, ${calculateScaleValue(
						get(element, 'canvasRenderConfig.windowCoordinates.height'),
						vHeight,
						get(element, 'canvasRenderConfig.canvasCoordinates.y', 0)
					)}px) rotate(${element.angle}deg)`,
					//@ts-ignore
					'--calculated-font-size': `${
						get(element, 'cropped')
							? calCropFontSize(
									get(element, 'fontSize', 8),
									get(element, 'fontCropData.fontCropWidth', 0),
									get(element, 'fontCropData.fontCropHeight', 0),
									get(element, 'fontCropData.fontEleWidth', 0),
									get(element, 'fontCropData.fontEleHeight', 0)
							  )
							: calculateNewFontSize(
									get(element, 'fontSize', 8),
									get(element, 'canvasRenderConfig.windowCoordinates.width', 0),
									get(element, 'canvasRenderConfig.windowCoordinates.height', 0)
							  )
					}px`,
					'--calculated-font-family': element.fontName,
				}}
				dangerouslySetInnerHTML={{ __html: element.text }}
			></div>
		</div>
	));
};

export default TextElementShowUntil;
